import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../context/OrgContext';
import { useNotification } from '../../../context/NotificationContext';
import { apiService } from '../../../services/apiService';
import { CafeStatus } from '../../../types';
import './DashboardStatusIndicator.css';

interface DashboardStatusIndicatorProps {
  onStatusChange?: (status: CafeStatus) => void;
}

const DashboardStatusIndicator: React.FC<DashboardStatusIndicatorProps> = ({ onStatusChange }) => {
  const { currentOrganization } = useOrg();
  const { showNotification } = useNotification();
  const [cafeStatus, setCafeStatus] = useState<CafeStatus>({
    isOpen: false,
    isBusy: false
  });
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (currentOrganization) {
      loadCafeStatus();
    }
  }, [currentOrganization]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const loadCafeStatus = async () => {
    if (!currentOrganization || !currentOrganization.urlName) {
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.getCafeStatus(currentOrganization.urlName);

      if (response.success && response.data) {
        setCafeStatus(response.data.cafeStatus);
        onStatusChange?.(response.data.cafeStatus);
      }
    } catch (err) {
      console.error('Failed to load cafe status:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBusy = async () => {
    if (!currentOrganization || loading) return;
    
    try {
      setLoading(true);
      
      const newStatus = { ...cafeStatus, isBusy: !cafeStatus.isBusy };
      const response = await apiService.updateCafeStatus(currentOrganization.urlName, newStatus);
      
      if (response.success) {
        setCafeStatus(newStatus);
        onStatusChange?.(newStatus);
        
        // Show notification for busy status change
        const statusText = newStatus.isBusy ? 'marked as busy' : 'marked as not busy';
        showNotification('info', `Cafe ${statusText}`, 3000);
      } else {
        showNotification('error', 'Failed to update busy status', 4000);
      }
    } catch (err) {
      console.error('Failed to update busy status:', err);
      showNotification('error', 'Failed to update busy status', 4000);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (cafeStatus.isBusy) return 'Busy';
    if (cafeStatus.isOpen) return 'Open';
    return 'Closed';
  };

  const getStatusColor = () => {
    if (cafeStatus.isBusy) return '#f59e0b'; // amber
    if (cafeStatus.isOpen) return '#10b981'; // green
    return '#ef4444'; // red
  };

  const getStatusIcon = () => {
    if (cafeStatus.isBusy) return '⚠️';
    if (cafeStatus.isOpen) return '🟢';
    return '🔴';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Don't render if organization is not available
  if (!currentOrganization) {
    return null;
  }

  return (
    <div className="dashboard-status-indicator">
      <div className="status-main">
        <div className="status-info">
          <div className="status-header">
            <div className="status-display">
              <span className="status-text">{getStatusDisplay()}</span>
              <span className="status-icon">{getStatusIcon()}</span>
            </div>
            {cafeStatus.isOpen && (
              <button
                onClick={toggleBusy}
                disabled={loading}
                className={`status-toggle-btn ${cafeStatus.isBusy ? 'busy-active' : ''}`}
                title={cafeStatus.isBusy ? 'Mark as normal operation' : 'Mark as busy'}
              >
                {loading ? '...' : cafeStatus.isBusy ? 'Normal' : 'Busy'}
              </button>
            )}
          </div>
          <div className="status-detail">
            {cafeStatus.isOpen ? (
              cafeStatus.isBusy ? (
                'Counter service'
              ) : (
                'Ready to take orders'
              )
            ) : (
              'Cafe is currently closed'
            )}
          </div>
        </div>
        
      
      </div>
      
      <div className="status-time">
        <div className="current-time">{formatTime(currentTime)}</div>
        <div className="current-date">{formatDate(currentTime)}</div>
      </div>
    </div>
  );
};

export default DashboardStatusIndicator;
